import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { renderUserAvatar } from "@/lib/avatar";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Archive, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useDrawer } from "@/components/RightDrawer";

const STATUSES = [
  { id: "active", label: "Active" },
  { id: "on_hold", label: "On hold" },
  { id: "completed", label: "Completed" },
  { id: "archived", label: "Archived" },
];
// Precompute once (module scope) so the filter doesn't rebuild the array
// on every render of the create-project dialog.
const SELECTABLE_STATUSES = STATUSES.filter((s) => s.id !== "archived");
const PRIORITIES = ["low", "medium", "high", "urgent"];
const PRIORITY_COLORS = {
  low: "bg-zinc-100 text-zinc-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-orange-100 text-orange-700",
  urgent: "bg-red-100 text-red-700",
};

export default function ProjectsView({ subscribe }) {
  const { openDrawer } = useDrawer();
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("active");

  const load = async () => {
    const params = filter === "all" ? { include_archived: true } : filter === "archived" ? { include_archived: true, status: "archived" } : { status: filter };
    const [p, m] = await Promise.all([api.get("/projects", { params }), api.get("/orgs/current/members")]);
    setItems(p.data);
    setMembers(m.data);
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((type) => { if (type.startsWith("project.")) load(); });
  }, [subscribe, filter]);

  const openDetail = (p) => openDrawer("Project", p.id, <ProjectDetail id={p.id} members={members} onChange={load}/>);

  const visible = useMemo(() => items, [items]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="projects-view">
      <div className="h-14 border-b flex items-center justify-between px-6 shrink-0">
        <h1 className="text-lg font-semibold flex items-center gap-2"><Briefcase className="w-5 h-5 text-[var(--brand-accent)]"/>Projects</h1>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-9 text-sm" data-testid="project-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_hold">On hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All (incl. archived)</SelectItem>
            </SelectContent>
          </Select>
          <NewProjectDialog members={members} onCreated={load}/>
        </div>
      </div>
      <div className="flex-1 overflow-auto scroll-thin p-6">
        {visible.length === 0 ? (
          <div className="text-sm text-zinc-500 border rounded-lg p-8 text-center">
            No projects yet. Create your first project to organize related tasks and collaborators.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visible.map((p) => <ProjectCard key={p.id} p={p} onClick={openDetail}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectCard({ p, onClick }) {
  const pct = p.task_counts?.total ? Math.round((p.task_counts.done / p.task_counts.total) * 100) : 0;
  return (
    <div onClick={() => onClick(p)} className="border rounded-lg p-4 cursor-pointer hover:shadow-md transition bg-white" data-testid={`project-card-${p.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{p.name}</div>
          <div className="text-xs text-zinc-500 truncate">{p.description || "No description"}</div>
        </div>
        <span className={`text-[10px] rounded-full px-2 py-0.5 shrink-0 ${PRIORITY_COLORS[p.priority] || ""}`}>{p.priority}</span>
      </div>
      <div className="flex items-center justify-between text-xs mt-3">
        <span className="text-zinc-500">{STATUSES.find((s) => s.id === p.status)?.label || p.status}</span>
        {p.due_date && <span className="text-zinc-400">Due {p.due_date}</span>}
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="flex -space-x-2">
          {(p.members || []).slice(0, 4).map((m) => m && (
            <img key={m.id} src={renderUserAvatar(m)} className="w-6 h-6 rounded-full border-2 border-white object-cover" alt="" title={m.name}/>
          ))}
        </div>
        <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
          <div className="h-full bg-[var(--brand-accent)]" style={{ width: `${pct}%` }}/>
        </div>
        <div className="text-[11px] text-zinc-500 mono shrink-0">{p.task_counts?.done || 0}/{p.task_counts?.total || 0}</div>
      </div>
    </div>
  );
}

function NewProjectDialog({ members, onCreated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", description: "", priority: "medium", status: "active", owner_id: "", member_ids: [], start_date: "", due_date: "" });

  const reset = () => setF({ name: "", description: "", priority: "medium", status: "active", owner_id: "", member_ids: [], start_date: "", due_date: "" });
  const create = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    try {
      const payload = { ...f, owner_id: f.owner_id || user?.id };
      await api.post("/projects", payload);
      setOpen(false); reset();
      toast.success("Project created");
      onCreated();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-[var(--brand-accent)] text-white" data-testid="new-project-btn"><Plus className="w-4 h-4 mr-1"/> New Project</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create project</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="project-name"/>
          <Textarea placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="project-description"/>
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="project-status"><SelectValue/></SelectTrigger>
              <SelectContent>{SELECTABLE_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
              <SelectTrigger data-testid="project-priority"><SelectValue/></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Select value={f.owner_id || undefined} onValueChange={(v) => setF({ ...f, owner_id: v })}>
            <SelectTrigger data-testid="project-owner"><SelectValue placeholder="Owner (defaults to you)"/></SelectTrigger>
            <SelectContent>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name}</SelectItem>)}</SelectContent>
          </Select>
          <div>
            <label className="text-xs text-zinc-500">Members</label>
            <div className="flex flex-wrap gap-1 mt-1 max-h-32 overflow-auto">
              {members.map((m) => (
                <button key={m.user_id} onClick={() => setF({ ...f, member_ids: f.member_ids.includes(m.user_id) ? f.member_ids.filter((x) => x !== m.user_id) : [...f.member_ids, m.user_id] })}
                  className={`text-xs rounded-full px-2 py-1 border ${f.member_ids.includes(m.user_id) ? "border-[var(--brand-accent)] bg-orange-50" : "border-zinc-200"}`}>
                  {m.user?.name}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-zinc-500">Start date</label>
              <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="project-start"/>
            </div>
            <div>
              <label className="text-xs text-zinc-500">Due date</label>
              <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="project-due"/>
            </div>
          </div>
          <Button className="w-full bg-[var(--brand-primary)] text-white" onClick={create} data-testid="project-create-submit">Create project</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProjectDetail({ id, members, onChange }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/projects/${id}`).then((r) => setData(r.data)); }, [id]);
  const patch = async (p) => {
    await api.patch(`/projects/${id}`, p);
    const r = await api.get(`/projects/${id}`);
    setData(r.data);
    onChange && onChange();
  };
  const archive = async () => {
    if (!window.confirm("Archive this project? Tasks remain but the project is hidden from the active list.")) return;
    await api.delete(`/projects/${id}`);
    onChange && onChange();
  };
  if (!data) return <div className="p-4 text-sm text-zinc-500">Loading…</div>;
  const p = data.project;
  return (
    <div className="p-4 space-y-4">
      <div>
        <Input value={p.name} onChange={(e) => setData({ ...data, project: { ...p, name: e.target.value } })}
          onBlur={(e) => patch({ name: e.target.value })} className="text-lg font-semibold border-0 px-0" data-testid="project-detail-name"/>
      </div>
      <Textarea value={p.description || ""} onChange={(e) => setData({ ...data, project: { ...p, description: e.target.value } })}
        onBlur={(e) => patch({ description: e.target.value })} placeholder="Add a description…"/>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase text-zinc-500">Status</label>
          <Select value={p.status} onValueChange={(v) => patch({ status: v })}>
            <SelectTrigger data-testid="project-detail-status"><SelectValue/></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Priority</label>
          <Select value={p.priority} onValueChange={(v) => patch({ priority: v })}>
            <SelectTrigger><SelectValue/></SelectTrigger>
            <SelectContent>{PRIORITIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs uppercase text-zinc-500">Start</label>
          <Input type="date" defaultValue={p.start_date || ""} onBlur={(e) => patch({ start_date: e.target.value || null })}/>
        </div>
        <div>
          <label className="text-xs uppercase text-zinc-500">Due</label>
          <Input type="date" defaultValue={p.due_date || ""} onBlur={(e) => patch({ due_date: e.target.value || null })}/>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase text-zinc-500">Owner</label>
        <Select value={p.owner_id || undefined} onValueChange={(v) => patch({ owner_id: v })}>
          <SelectTrigger><SelectValue placeholder="Owner"/></SelectTrigger>
          <SelectContent>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs uppercase text-zinc-500">Members</label>
        <div className="flex flex-wrap gap-1 mt-1">
          {members.map((m) => (
            <button key={m.user_id} onClick={() => patch({ member_ids: (p.member_ids || []).includes(m.user_id) ? p.member_ids.filter((x) => x !== m.user_id) : [...(p.member_ids || []), m.user_id] })}
              className={`text-xs rounded-full px-2 py-1 border ${(p.member_ids || []).includes(m.user_id) ? "border-[var(--brand-accent)] bg-orange-50" : "border-zinc-200"}`}>{m.user?.name}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs uppercase text-zinc-500 mb-2">Tasks in this project</div>
        {(data.tasks || []).length === 0 ? (
          <div className="text-xs text-zinc-400">No tasks yet. Assign a task to this project from the Tasks module.</div>
        ) : (
          <div className="space-y-1">
            {(data.tasks || []).map((t) => (
              <div key={t.id} className="text-sm flex items-center gap-2 border rounded p-2">
                <span className={`text-[10px] rounded-full px-1.5 py-0.5 bg-zinc-100`}>{t.status}</span>
                <span className="flex-1 truncate">{t.title}</span>
                <div className="flex -space-x-1">
                  {(t.assignees || []).slice(0, 3).map((a) => a && <img key={a.id} src={renderUserAvatar(a)} className="w-5 h-5 rounded-full border-2 border-white object-cover" alt="" title={a.name}/>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {!p.archived && (
        <Button variant="outline" size="sm" onClick={archive} data-testid="project-archive-btn">
          <Archive className="w-4 h-4 mr-1"/> Archive project
        </Button>
      )}
    </div>
  );
}
